/* MagneGlow legal pages.
   Takes the policy HTML that Shopify renders (Settings > Policies) and presents it with the
   MagneGlow layout: intro, sticky contents, numbered sections. It never adds, removes or rewrites
   policy wording. Headings keep their text; navigation is built from the H2s found on the page. */
(() => {
  const reveal = () => document.documentElement.classList.remove('mg-policy-pending');
  const cfgEl = document.getElementById('mg-policy-config');
  if (!cfgEl) { reveal(); return; }

  let cfg = {};
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { /* use defaults */ }

  const icon = (d) => `<svg class="mg-policy__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="${d}"></path></svg>`;
  const CHEVRON = 'm5 8 5 5 5-5';
  const ARROW_DOWN = 'M10 4v12M5 11l5 5 5-5';
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  const slugify = (t) => t.toLowerCase().replace(/&/g, ' and ').replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
  const NUM = /^\s*\d+[.)]\s+/;

  const scrollParent = (node) => {
    let p = node.parentElement;
    while (p && p !== document.body) {
      const o = getComputedStyle(p).overflowY;
      if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight) return p;
      p = p.parentElement;
    }
    return window;
  };

  const build = () => {
    const container = document.querySelector('.shopify-policy__container');
    if (!container || container.dataset.mgDone) { reveal(); return; }
    const bodyEl = container.querySelector('.shopify-policy__body .rte') || container.querySelector('.shopify-policy__body');
    if (!bodyEl) { reveal(); return; }
    container.dataset.mgDone = '1';

    const nativeTitleEl = container.querySelector('.shopify-policy__title h1, .shopify-policy__title');
    const nativeTitle = nativeTitleEl ? nativeTitleEl.textContent.trim() : '';

    // Loose text nodes become paragraphs so that nothing is dropped.
    [...bodyEl.childNodes].forEach((n) => {
      if (n.nodeType === 3 && n.textContent.trim()) {
        const p = document.createElement('p');
        p.textContent = n.textContent.trim();
        bodyEl.replaceChild(p, n);
      }
    });
    const kids = [...bodyEl.children];

    // The policy's own title and "Last updated" line move into the intro (shown once, not twice).
    let titleText = '';
    const h1 = kids.slice(0, 3).find((k) => k.tagName === 'H1');
    if (h1) { titleText = h1.textContent.trim(); h1.remove(); }
    let updatedText = '';
    const upd = kids.slice(0, 6).find((k) => /^\s*last updated/i.test(k.textContent) && k.textContent.length < 90);
    if (upd) { updatedText = upd.textContent.trim(); upd.remove(); }

    const rest = kids.filter((k) => k.isConnected && k.parentNode === bodyEl);
    const lead = [];
    const sections = [];
    rest.forEach((k) => {
      if (k.tagName === 'H2') sections.push({ h: k, nodes: [] });
      else if (sections.length) sections[sections.length - 1].nodes.push(k);
      else lead.push(k);
    });

    // Stable, unique ids from the heading text.
    const used = new Set([...document.querySelectorAll('[id]')].map((n) => n.id));
    sections.forEach((s, i) => {
      const raw = s.h.textContent.trim();
      s.numbered = NUM.test(raw);
      s.label = raw.replace(NUM, '');
      let id = s.h.id || slugify(s.label);
      let n = 2;
      const base = id;
      while (used.has(id) && id !== s.h.id) id = `${base}-${n++}`;
      used.add(id);
      s.id = id;
      s.index = String(i + 1).padStart(2, '0');
    });

    // ---- Build the page ----
    const root = el('div', 'mg mg-policy');
    root.dataset.bg = cfg.bg || 'tan';

    const intro = el('header', 'mg-policy__intro');
    const inner = el('div', 'mg-policy__intro-inner');
    if (cfg.eyebrow) inner.appendChild(el('p', 'mg-policy__eyebrow', cfg.eyebrow));
    inner.appendChild(el('h1', 'mg-policy__title', titleText || nativeTitle));
    if (cfg.intro) inner.appendChild(el('p', 'mg-policy__blurb', cfg.intro));
    if (updatedText) inner.appendChild(el('p', 'mg-policy__updated', updatedText));
    intro.appendChild(inner);
    root.appendChild(intro);

    const grid = el('div', 'mg-policy__body');
    const main = el('main', 'mg-policy__main');
    main.id = 'mg-policy-main';

    let nav = null;
    let contents = null;
    let toggle = null;
    let jump = null;
    const navLinks = [];

    if (sections.length > 1) {
      nav = el('nav', 'mg-policy__nav');
      nav.setAttribute('aria-label', 'Policy sections');
      nav.appendChild(el('p', 'mg-policy__nav-label', cfg.navLabel));
      const ol = el('ol');
      sections.forEach((s) => {
        const li = el('li');
        const a = el('a', null, s.label);
        a.href = '#' + s.id;
        a.dataset.id = s.id;
        li.appendChild(a);
        ol.appendChild(li);
        navLinks.push(a);
      });
      nav.appendChild(ol);

      contents = el('div', 'mg-policy__contents');
      contents.id = 'mg-policy-contents';
      toggle = el('button', 'mg-policy__toggle');
      toggle.type = 'button';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', 'mg-policy-list');
      toggle.innerHTML = `<span></span>${icon(CHEVRON)}`;
      toggle.firstChild.textContent = cfg.contentsLabel;
      const list = el('ol', 'mg-policy__list');
      list.id = 'mg-policy-list';
      list.hidden = true;
      sections.forEach((s) => {
        const li = el('li');
        const a = document.createElement('a');
        a.href = '#' + s.id;
        a.dataset.id = s.id;
        a.innerHTML = '<span class="mg-policy__num"></span><span></span>';
        a.firstChild.textContent = s.index;
        a.lastChild.textContent = s.label;
        li.appendChild(a);
        list.appendChild(li);
        navLinks.push(a);
      });
      contents.append(toggle, list);

      jump = document.createElement('a');
      jump.className = 'mg-policy__jump';
      jump.href = '#mg-policy-contents';
      jump.innerHTML = `<span></span>${icon(ARROW_DOWN)}`;
      jump.firstChild.textContent = cfg.jumpLabel;
      inner.appendChild(jump);

      grid.append(nav, contents);
    } else {
      grid.classList.add('mg-policy__body--single');
    }

    if (lead.length) {
      const leadEl = el('div', 'mg-policy__lead');
      lead.forEach((n) => leadEl.appendChild(n));
      main.appendChild(leadEl);
    }

    sections.forEach((s, i) => {
      const sec = el('section', 'mg-policy__section');
      sec.id = s.id;
      if (!s.numbered) sec.appendChild(el('p', 'mg-policy__eyebrow-section', `${s.index} — ${s.label}`));
      if (s.h.id === s.id) s.h.removeAttribute('id');
      sec.appendChild(s.h);
      const isContact = i === sections.length - 1 && /contact/i.test(s.label);
      if (isContact && s.nodes.length) {
        const panel = el('div', 'mg-policy__panel');
        s.nodes.forEach((n) => panel.appendChild(n));
        sec.appendChild(panel);
      } else {
        s.nodes.forEach((n) => sec.appendChild(n));
      }
      main.appendChild(sec);
    });

    // Anything left in the original body (should be nothing) is kept, not discarded.
    [...bodyEl.children].forEach((n) => main.appendChild(n));

    grid.appendChild(main);
    root.appendChild(grid);
    container.parentNode.replaceChild(root, container);

    // ---- Behaviour ----
    const headerGroup = document.getElementById('header-group');
    const setTop = () => {
      const h = headerGroup ? Math.round(headerGroup.getBoundingClientRect().height) : 0;
      root.style.setProperty('--mgp-top', (h + 24) + 'px');
    };
    setTop();
    window.addEventListener('resize', setTop);

    const setCurrent = (id) => navLinks.forEach((a) => {
      if (a.dataset.id === id) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });

    const go = (id, instant) => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: reduce || instant ? 'auto' : 'smooth', block: 'start' });
      const heading = target.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      try { history.replaceState(null, '', '#' + id); } catch (e) { /* ignore */ }
      setCurrent(id);
    };

    navLinks.forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault();
      // Collapse the mobile list first so the scroll target is measured on the final layout.
      if (toggle && a.closest('.mg-policy__list')) {
        toggle.setAttribute('aria-expanded', 'false');
        document.getElementById('mg-policy-list').hidden = true;
      }
      go(a.dataset.id);
    }));

    if (toggle) {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.getElementById('mg-policy-list').hidden = !open;
      });
    }
    if (jump) {
      jump.addEventListener('click', (e) => {
        e.preventDefault();
        toggle.setAttribute('aria-expanded', 'true');
        document.getElementById('mg-policy-list').hidden = false;
        contents.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        toggle.focus({ preventScroll: true });
      });
    }

    if (sections.length) {
      const scroller = scrollParent(root);
      const onScroll = () => {
        let cur = sections[0].id;
        sections.forEach((s) => {
          const node = document.getElementById(s.id);
          if (node && node.getBoundingClientRect().top < window.innerHeight * 0.3) cur = s.id;
        });
        const atEnd = scroller === window
          ? window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
          : scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4;
        if (atEnd) cur = sections[sections.length - 1].id;
        setCurrent(cur);
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    reveal();
    const hash = decodeURIComponent((location.hash || '').slice(1));
    if (hash && document.getElementById(hash)) requestAnimationFrame(() => go(hash, true));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build); else build();
})();
